/**
 * Firmio Meet API client.
 *
 * Three groups, matching the three routers: the colleagues' side (list,
 * create, host actions — app Bearer), the public door (lobby preview and
 * join — optional app Bearer), and the in-call session (state poll, leave,
 * audio chunks, live transcript — the participant token the join handed
 * back, passed explicitly because a guest has no auth store to read from).
 */
import { apiBase } from './apiBase';
import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { VoiceTranscriptSegment } from './voiceCallsApi';

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function jsonHeaders(): Record<string, string> {
  return { ...authHeaders(), 'Content-Type': 'application/json' };
}

function participantHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` };
}

export type VideoMeetingStatus = 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';
export type MeetAdmission = 'WAITING' | 'ADMITTED' | 'DENIED';

export interface MeetParticipant {
  id: string;
  userId: string | null;
  displayName: string;
  guest: boolean;
  admission: MeetAdmission;
  joinedAt: string;
  admittedAt: string | null;
  leftAt: string | null;
}

export interface MeetInvite {
  id: string;
  userId: string | null;
  email: string | null;
  name: string | null;
}

/** One line of the in-meeting text chat. */
export interface MeetChatMessage {
  id: string;
  participantId: string;
  userId: string | null;
  senderName: string;
  text: string;
  /** Server time; monotonic, which is what the catch-up poll pages on. */
  createdAt: string;
}

/** A transcript line; same shape as a voice line so the transcript view is shared. */
export type MeetSegment = VoiceTranscriptSegment & { participantId: string };

export interface VideoMeetingSummary {
  id: string;
  code: string;
  title: string;
  status: VideoMeetingStatus;
  hostId: string;
  hostName: string;
  scheduledAt: string;
  durationMinutes: number;
  clientId: string | null;
  clientName: string | null;
  projectId: string | null;
  projectName: string | null;
  companyId: string | null;
  logMeetingId: string | null;
  logProcessedAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationMs: number;
  participantCount: number;
  segmentCount: number;
  /** Recordings uploaded to Drive and ready to play. */
  recordingCount: number;
  joinUrl: string;
  /** Host, invited users and joined users — everyone with a seat at the table. */
  attendeeUserIds: string[];
}

export interface VideoMeetingDetail extends VideoMeetingSummary {
  participants: MeetParticipant[];
  invites: MeetInvite[];
  segments: MeetSegment[];
  /** Host, admitted colleague or canManage: may admit, end, cancel. */
  canManage: boolean;
  transcriptionEnabled: boolean;
}

export interface CreateMeetingInput {
  title?: string;
  scheduledAt?: string;
  durationMinutes?: number;
  inviteUserIds?: string[];
  inviteEmails?: string[];
  clientId?: string | null;
  projectId?: string | null;
  companyId?: string | null;
}

export interface MeetPublicInfo {
  id: string;
  code: string;
  title: string;
  status: VideoMeetingStatus;
  hostName: string;
  scheduledAt: string;
  durationMinutes: number;
  startedAt: string | null;
  endedAt: string | null;
  joinable: boolean;
  opensAt: string;
  participantCount: number;
  transcriptionEnabled: boolean;
  livekitConfigured: boolean;
  /** The logged-in colleague behind the request, if any. */
  me: { name: string; userId: string } | null;
}

export interface MeetGrant {
  token: string;
  url: string;
  room: string;
}

export interface MeetJoinResult {
  participantToken: string;
  participantId: string;
  admission: MeetAdmission;
  livekit: MeetGrant | null;
  meeting: { id: string; code: string; title: string; status: VideoMeetingStatus; hostId: string };
  transcriptionEnabled: boolean;
  me: { displayName: string; guest: boolean; userId: string | null };
}

export interface MeetSessionState {
  admission: MeetAdmission;
  meetingStatus: VideoMeetingStatus;
  livekit: MeetGrant | null;
  hostInside: boolean;
}

// --- Colleagues ---------------------------------------------------------------

export async function fetchMeetings(input: {
  tab?: 'upcoming' | 'past';
  from?: string;
  to?: string;
  limit?: number;
  q?: string;
  companyId?: string | null;
}): Promise<{ meetings: VideoMeetingSummary[]; transcriptionEnabled: boolean }> {
  const params = new URLSearchParams();
  if (input.tab) params.set('tab', input.tab);
  if (input.from) params.set('from', input.from);
  if (input.to) params.set('to', input.to);
  if (input.limit) params.set('limit', String(input.limit));
  if (input.q?.trim()) params.set('q', input.q.trim());
  if (input.companyId) params.set('companyId', input.companyId);
  const qs = params.toString();
  const res = await apiFetch<
    Envelope<{ meetings: VideoMeetingSummary[]; transcriptionEnabled: boolean }>
  >(`/api/meet${qs ? `?${qs}` : ''}`, { headers: authHeaders() });
  return {
    meetings: res.data?.meetings ?? [],
    transcriptionEnabled: res.data?.transcriptionEnabled ?? false,
  };
}

export async function fetchMeeting(id: string): Promise<VideoMeetingDetail> {
  const res = await apiFetch<Envelope<VideoMeetingDetail>>(`/api/meet/${encodeURIComponent(id)}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function createMeeting(input: CreateMeetingInput): Promise<VideoMeetingSummary> {
  const res = await apiFetch<Envelope<VideoMeetingSummary>>('/api/meet', {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updateMeeting(
  id: string,
  patch: Pick<
    CreateMeetingInput,
    | 'title'
    | 'scheduledAt'
    | 'durationMinutes'
    | 'clientId'
    | 'projectId'
    // The guest list as it should be after the edit, not a delta.
    | 'inviteUserIds'
    | 'inviteEmails'
  >,
): Promise<VideoMeetingSummary> {
  const res = await apiFetch<Envelope<VideoMeetingSummary>>(`/api/meet/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify(patch),
  });
  return res.data;
}

async function hostAction<T>(id: string, action: string): Promise<T> {
  const res = await apiFetch<Envelope<T>>(`/api/meet/${encodeURIComponent(id)}/${action}`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return res.data;
}

export const endMeeting = (id: string) => hostAction<{ ended: boolean }>(id, 'end');
export const cancelMeeting = (id: string) => hostAction<{ cancelled: boolean }>(id, 'cancel');
export const writeMeetingLog = (id: string) =>
  hostAction<{ logMeetingId: string | null; skipped: boolean }>(id, 'log');

export async function fetchWaiting(id: string): Promise<MeetParticipant[]> {
  const res = await apiFetch<Envelope<{ waiting: MeetParticipant[] }>>(
    `/api/meet/${encodeURIComponent(id)}/waiting`,
    { headers: authHeaders() },
  );
  return res.data?.waiting ?? [];
}

export function admitParticipant(id: string, participantId: string): Promise<MeetParticipant> {
  return hostAction<MeetParticipant>(id, `participants/${encodeURIComponent(participantId)}/admit`);
}

export function denyParticipant(id: string, participantId: string): Promise<MeetParticipant> {
  return hostAction<MeetParticipant>(id, `participants/${encodeURIComponent(participantId)}/deny`);
}

export type MeetModeration = 'mute' | 'camera-off' | 'remove';

/**
 * Host-side moderation of someone in the room: mute their microphone, turn
 * their camera off (both reversible by the person themselves) or put them
 * out of the meeting. `participantId` is the LiveKit identity, i.e. the
 * participant row id.
 */
export function moderateParticipant(
  id: string,
  participantId: string,
  action: MeetModeration,
): Promise<{ changed?: boolean; removed?: boolean }> {
  return hostAction(id, `participants/${encodeURIComponent(participantId)}/${action}`);
}

export async function deleteMeeting(id: string): Promise<void> {
  await apiFetch<Envelope<{ deleted: boolean }>>(`/api/meet/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}

// --- The door ------------------------------------------------------------------

export async function fetchMeetPublic(code: string): Promise<MeetPublicInfo> {
  const res = await apiFetch<Envelope<MeetPublicInfo>>(
    `/api/meet/public/${encodeURIComponent(code)}`,
    { headers: authHeaders() },
  );
  return res.data;
}

/** Colleagues send their app token and no name; guests send a name. */
export async function joinMeet(
  code: string,
  input: { name?: string; email?: string },
): Promise<MeetJoinResult> {
  const res = await apiFetch<Envelope<MeetJoinResult>>(
    `/api/meet/public/${encodeURIComponent(code)}/join`,
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(input) },
  );
  return res.data;
}

// --- Inside ----------------------------------------------------------------------

export async function fetchMeetSessionState(token: string): Promise<MeetSessionState> {
  const res = await apiFetch<Envelope<MeetSessionState>>('/api/meet/session/state', {
    headers: participantHeaders(token),
  });
  return res.data;
}

export async function leaveMeet(token: string): Promise<void> {
  await apiFetch<Envelope<{ left: boolean }>>('/api/meet/session/leave', {
    method: 'POST',
    headers: participantHeaders(token),
  });
}

export async function uploadMeetChunk(input: {
  token: string;
  audio: Blob;
  startedAt: Date;
  durationMs: number;
}): Promise<MeetSegment | null> {
  const params = new URLSearchParams({
    startedAt: input.startedAt.toISOString(),
    durationMs: String(Math.max(1, Math.round(input.durationMs))),
  });
  const res = await apiFetch<Envelope<{ segment: MeetSegment | null }>>(
    `/api/meet/session/segments?${params.toString()}`,
    {
      method: 'POST',
      headers: {
        ...participantHeaders(input.token),
        'Content-Type': input.audio.type || 'audio/webm',
      },
      body: input.audio,
    },
  );
  return res.data?.segment ?? null;
}

export async function fetchMeetSegmentsSince(
  token: string,
  since: string | null,
): Promise<{ segments: MeetSegment[]; ended: boolean }> {
  const qs = since ? `?since=${encodeURIComponent(since)}` : '';
  const res = await apiFetch<Envelope<{ segments: MeetSegment[]; ended: boolean }>>(
    `/api/meet/session/segments${qs}`,
    { headers: participantHeaders(token) },
  );
  return { segments: res.data?.segments ?? [], ended: res.data?.ended ?? false };
}

// --- Chat (everyone in the room; kept after the call) ------------------------

export async function sendMeetChatMessage(token: string, text: string): Promise<MeetChatMessage> {
  const res = await apiFetch<Envelope<{ message: MeetChatMessage }>>('/api/meet/session/chat', {
    method: 'POST',
    headers: { ...participantHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  return res.data.message;
}

export async function fetchMeetChatSince(
  token: string,
  since: string | null,
): Promise<{ messages: MeetChatMessage[]; ended: boolean }> {
  const qs = since ? `?since=${encodeURIComponent(since)}` : '';
  const res = await apiFetch<Envelope<{ messages: MeetChatMessage[]; ended: boolean }>>(
    `/api/meet/session/chat${qs}`,
    { headers: participantHeaders(token) },
  );
  return { messages: res.data?.messages ?? [], ended: res.data?.ended ?? false };
}

/** The whole chat of a meeting, for the history page (app Bearer). */
export async function fetchMeetingChat(meetingId: string): Promise<MeetChatMessage[]> {
  const res = await apiFetch<Envelope<{ messages: MeetChatMessage[] }>>(
    `/api/meet/${encodeURIComponent(meetingId)}/chat`,
    { headers: authHeaders() },
  );
  return res.data?.messages ?? [];
}

// --- Recordings (host's browser → backend → Google Drive) --------------------

export type MeetRecordingStatus = 'RECORDING' | 'UPLOADING' | 'READY' | 'FAILED';

export interface MeetRecording {
  id: string;
  meetingId: string;
  startedById: string;
  status: MeetRecordingStatus;
  mimeType: string;
  startedAt: string;
  endedAt: string | null;
  durationMs: number;
  bytes: number;
  /** Range-capable stream for the player once READY. */
  streamUrl: string | null;
  /** Same stream as an attachment (save link) once READY. */
  downloadUrl: string | null;
  error: string | null;
}

export async function fetchRecordings(
  meetingId: string,
): Promise<{ enabled: boolean; recordings: MeetRecording[] }> {
  const res = await apiFetch<Envelope<{ enabled: boolean; recordings: MeetRecording[] }>>(
    `/api/meet/${encodeURIComponent(meetingId)}/recordings`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function startRecording(meetingId: string, mimeType: string): Promise<MeetRecording> {
  const res = await apiFetch<Envelope<MeetRecording>>(
    `/api/meet/${encodeURIComponent(meetingId)}/recordings`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify({ mimeType }) },
  );
  return res.data;
}

/** One MediaRecorder chunk, raw body; the server appends it in `seq` order. */
export async function uploadRecordingChunk(
  recordingId: string,
  seq: number,
  chunk: Blob,
): Promise<void> {
  const res = await fetch(
    `${apiBase()}/api/meet/_/recordings/${encodeURIComponent(recordingId)}/chunks?seq=${seq}`,
    {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': chunk.type || 'application/octet-stream' },
      body: chunk,
    },
  );
  if (!res.ok) {
    const err = new Error(`chunk ${seq} failed (HTTP ${res.status})`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
}

export async function finishRecording(
  meetingId: string,
  recordingId: string,
): Promise<MeetRecording> {
  const res = await apiFetch<Envelope<MeetRecording>>(
    `/api/meet/${encodeURIComponent(meetingId || '_')}/recordings/${encodeURIComponent(recordingId)}/finish`,
    { method: 'POST', headers: authHeaders(), body: '{}' },
  );
  return res.data;
}
